// context/SocketContext.js
import { createContext, useContext, useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { secureLog } from '../utils/secureLogger';

const SocketContext = createContext({
  socket: null,
  connected: false,
  notifications: [],
  addNotification: () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  clearNotification: () => {},
  clearAllNotifications: () => {},
  getUnreadCount: () => 0,
  trackOrder: () => {},
  joinRoom: () => {},
  leaveRoom: () => {},
});

export const SocketProvider = ({ children }) => {
  // Get auth data - this should work since SocketProvider is inside AuthProvider
  const authContext = useAuth();
  const { user, loading } = authContext;
  
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const socketRef = useRef(null);
  const connectionAttemptRef = useRef(false);
  const failureCountRef = useRef(0);
  const disabledRef = useRef(false);

  // Helper functions for notifications
  const addNotification = (notification) => {
    const newNotification = {
      ...notification,
      id: Date.now() + Math.random(),
      read: false,
      timestamp: new Date(notification.timestamp || Date.now()),
    };
    setNotifications(prev => [newNotification, ...prev].slice(0, 50));
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, read: true }))
    );
  };

  const clearNotification = (notificationId) => {
    setNotifications(prev =>
      prev.filter(notif => notif.id !== notificationId)
    );
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const getUnreadCount = () => {
    return notifications.filter(notif => !notif.read).length;
  };

  // Initialize socket connection (singleton pattern)
  useEffect(() => {
    if (loading) return;

    // Prevent multiple socket creation
    if (!socketRef.current && !connectionAttemptRef.current) {
      connectionAttemptRef.current = true;

      // Check if WebSocket features are enabled or if we've disabled due to failures
      const socketEnabled = process.env.NEXT_PUBLIC_ENABLE_WEBSOCKET === 'true';

      if (!socketEnabled || disabledRef.current) {
        secureLog('log', socketEnabled ? 'WebSocket temporarily disabled due to failures' : 'WebSocket features disabled, skipping socket connection');
        connectionAttemptRef.current = false;
        return;
      }

      secureLog('log', 'Creating new socket connection...');

      // Get authentication token for socket connection
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

      const newSocket = io(process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000', {
        autoConnect: false,
        auth: {
          token: token, // Pass authentication token
          userId: user?._id || user?.id || ''
        },
        reconnection: true,
        reconnectionAttempts: 3, // Reduced for faster failure
        reconnectionDelay: 3000, // Increased delay
        reconnectionDelayMax: 10000,
        timeout: 10000, // Reduced timeout for faster failure
        transports: ['websocket'], // Only secure websocket, no polling fallback
        forceNew: true, // Always create new connection for security
        upgrade: true,
        rememberUpgrade: true
      });
      
      socketRef.current = newSocket;
      setSocket(newSocket);
      connectionAttemptRef.current = false;
    }
  }, [loading, user]);

  // Manage socket events and connection
  useEffect(() => {
    if (!socket || loading) return;

    const handleConnect = () => {
      setConnected(true);
      secureLog('log', '✅ Socket connected successfully');
      
      // Validate user before joining rooms
      if (user && (user._id || user.id)) {
        const userData = {
          userId: user._id || user.id,
          role: user.role || 'user',
          isAdmin: user.isAdmin === true || user.role === 'admin'
        };
        
        if (userData.isAdmin) {
          socket.emit('join-admin', userData);
        } else {
          socket.emit('join-user', userData);
        }
      }
    };

    const handleDisconnect = (reason) => {
      setConnected(false);
      secureLog('warn', `❌ Socket disconnected: ${reason}`);
    };

    const handleConnectError = (error) => {
      setConnected(false);
      failureCountRef.current += 1;

      if (failureCountRef.current >= 3) {
        secureLog('warn', `❌ Socket connection failed ${failureCountRef.current} times, disabling WebSocket features`);
        disabledRef.current = true;
        if (socket) {
          socket.disconnect();
          socket.close();
        }
      } else {
        secureLog('warn', `🔄 Socket connection error (${failureCountRef.current}/3), will retry: ${error.message}`);
      }
    };

    const handleReconnect = (attemptNumber) => {
      secureLog('log', `🔄 Socket reconnected after ${attemptNumber} attempts`);
    };

    const handleReconnectError = (error) => {
      secureLog('warn', `❌ Socket reconnection failed: ${error.message}`);
    };

    // Admin notifications
    const handleNewOrder = (notification) => {
      if (user?.isAdmin || user?.role === 'admin') {
        addNotification(notification);
        toast.success(notification.message, { duration: 6000, icon: '🛒' });
      }
    };

    const handleNewTicket = (notification) => {
      if (user?.isAdmin || user?.role === 'admin') {
        addNotification(notification);
        toast.success(notification.message, { duration: 6000, icon: '🎫' });
      }
    };

    const handlePaymentConfirmation = (notification) => {
      if (user?.isAdmin || user?.role === 'admin') {
        addNotification(notification);
        toast.error(notification.message, { duration: 8000, icon: '💳' });
      }
    };

    const handleLowStock = (notification) => {
      if (user?.isAdmin || user?.role === 'admin') {
        addNotification(notification);
        toast.error(notification.message, { duration: 6000, icon: '📦' });
      }
    };

    // User notifications
    const handleOrderUpdate = (notification) => {
      addNotification(notification);
      toast.success(notification.message, { duration: 5000, icon: '📋' });
    };

    const handlePaymentConfirmed = (notification) => {
      addNotification(notification);
      toast.success(notification.message, { duration: 6000, icon: '✅' });
    };

    const handleOrderCancelled = (notification) => {
      addNotification(notification);
      toast.error(notification.message, { duration: 6000, icon: '❌' });
    };

    const handleTicketResponse = (notification) => {
      addNotification(notification);
      toast.success(notification.message, { duration: 5000, icon: '💬' });
    };

    const handleSystemAnnouncement = (notification) => {
      addNotification(notification);
      const toastType = notification.priority === 'urgent' || notification.priority === 'high' ? 'error' : 'success';
      toast[toastType](notification.message, { duration: 8000, icon: '📢' });
    };

    const handleUserNotification = (notification) => {
      addNotification(notification);
      toast.success(notification.message);
    };

    const handleAdminNotification = (notification) => {
      if (user?.isAdmin || user?.role === 'admin') {
        addNotification(notification);
        toast.success(notification.message);
      }
    };

    // Attach event listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('reconnect', handleReconnect);
    socket.on('reconnect_error', handleReconnectError);
    socket.on('new-order', handleNewOrder);
    socket.on('new-ticket', handleNewTicket);
    socket.on('payment-confirmation', handlePaymentConfirmation);
    socket.on('low-stock', handleLowStock);
    socket.on('order-update', handleOrderUpdate);
    socket.on('payment-confirmed', handlePaymentConfirmed);
    socket.on('order-cancelled', handleOrderCancelled);
    socket.on('ticket-response', handleTicketResponse);
    socket.on('system-announcement', handleSystemAnnouncement);
    socket.on('user-notification', handleUserNotification);
    socket.on('admin-notification', handleAdminNotification);

    // Connect/disconnect based on user status with error handling
    if (user && !connected) {
      try {
        socket.connect();
        secureLog('log', '🔌 Attempting socket connection...');
      } catch (error) {
        secureLog('error', '❌ Failed to initiate socket connection:', error);
      }
    } else if (!user && connected) {
      socket.disconnect();
      setNotifications([]);
    }

    // Cleanup
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('reconnect', handleReconnect);
      socket.off('reconnect_error', handleReconnectError);
      socket.off('new-order', handleNewOrder);
      socket.off('new-ticket', handleNewTicket);
      socket.off('payment-confirmation', handlePaymentConfirmation);
      socket.off('low-stock', handleLowStock);
      socket.off('order-update', handleOrderUpdate);
      socket.off('payment-confirmed', handlePaymentConfirmed);
      socket.off('order-cancelled', handleOrderCancelled);
      socket.off('ticket-response', handleTicketResponse);
      socket.off('system-announcement', handleSystemAnnouncement);
      socket.off('user-notification', handleUserNotification);
      socket.off('admin-notification', handleAdminNotification);
    };
  }, [socket, user, loading]);

  // Clean up socket on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current.close();
        socketRef.current = null;
      }
      connectionAttemptRef.current = false;
    };
  }, []);

  // Emit functions
  const trackOrder = (orderNumber) => {
    if (socket && connected) {
      socket.emit('track-order', { orderNumber });
    }
  };

  const joinRoom = (room) => {
    if (socket && connected) {
      socket.emit('join-room', { room });
    }
  };

  const leaveRoom = (room) => {
    if (socket && connected) {
      socket.emit('leave-room', { room });
    }
  };

  const value = {
    socket,
    connected,
    notifications,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
    getUnreadCount,
    trackOrder,
    joinRoom,
    leaveRoom,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  // Handle SSR/SSG where React context might not be available
  if (typeof window === 'undefined') {
    return {
      socket: null,
      connected: false,
      notifications: [],
      addNotification: () => {},
      markAsRead: () => {},
      markAllAsRead: () => {},
      clearNotification: () => {},
      clearAllNotifications: () => {},
      getUnreadCount: () => 0,
      trackOrder: () => {},
      joinRoom: () => {},
      leaveRoom: () => {},
    };
  }

  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};